import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { MarqueeRow, StageFX } from "./CasinoFX";
import "./arcade-anim.css";

/* 🎲 realistic CSS 3D die face — pips for 1–6, bold number for 0 & 7–9 */
const DieFace = ({ v, won }) => {
  const pips = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] }[v];
  const c = won == null ? "#e5e7eb" : won ? "#089981" : "#F23645";
  return (
    <div className="w-14 h-14 rounded-xl grid place-items-center"
      style={{ background: "linear-gradient(145deg,#ffffff,#cbd5e1)", boxShadow: `inset -3px -4px 8px rgba(0,0,0,.25), inset 2px 3px 6px rgba(255,255,255,.9), 0 6px 14px rgba(0,0,0,.5)${won ? ", 0 0 18px rgba(8,153,129,.6)" : ""}` }}>
      {pips ? (
        <div className="grid grid-cols-3 grid-rows-3 w-9 h-9">
          {Array.from({ length: 9 }).map((_, i) => (
            <span key={i} className="grid place-items-center">
              {pips.includes(i) && <span className="w-2 h-2 rounded-full" style={{ background: c === "#e5e7eb" ? "#111827" : c, boxShadow: "inset 0 -1px 2px rgba(0,0,0,.5)" }} />}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-2xl font-black font-mono" style={{ color: c === "#e5e7eb" ? "#111827" : c }}>{v}</span>
      )}
    </div>
  );
};

const Dealer = ({ rolling }) => (
  <g transform="translate(160,0)">
    {/* dealer */}
    <g className={`arc-fig ${rolling ? "" : "arc-bob"}`}>
      <circle cx="0" cy="34" r="11" fill="#E8B88A" />
      <circle cx="-4" cy="32" r="1.5" fill="#0B0E14" />
      <circle cx="4" cy="32" r="1.5" fill="#0B0E14" />
      <path d="M -11 29 A 11 11 0 0 1 11 29" fill="#1b2130" />
      <path d="M -16 78 L -11 46 Q 0 42 11 46 L 16 78 Z" fill="#1b2130" />
      <path d="M -4 48 L 0 52 L 4 48 L 0 46 Z" fill="#F23645" />
      {/* arms holding the cup */}
      <line x1="-12" y1="52" x2="-22" y2="66" stroke="#1b2130" strokeWidth="5" strokeLinecap="round" />
      <line x1="12" y1="52" x2="22" y2="66" stroke="#1b2130" strokeWidth="5" strokeLinecap="round" />
    </g>
    {/* the cup */}
    <g className={rolling ? "arc-shake" : ""}>
      <path d="M -14 62 L 14 62 L 9 82 L -9 82 Z" fill="#F7B731" stroke="#0B0E14" strokeWidth="1" />
      <ellipse cx="0" cy="62" rx="14" ry="3.5" fill="#d99c17" />
    </g>
    {/* table */}
    <rect x="-120" y="84" width="240" height="8" rx="4" fill="#0f5132" />
    <rect x="-130" y="90" width="260" height="14" rx="4" fill="#161b29" />
  </g>
);

/* 🎲 Dice Duel — animated dealer shakes the cup; you call the next tick's last
   digit OVER or UNDER the bar (real Deriv DIGITOVER/DIGITUNDER). Over/Under
   scanner shows how recent digits split around YOUR bar on every market. */
const BARS = [2, 3, 4, 5, 6, 7];
/* 🎯 50%+ ONLY — win chance per side (digits strictly over / strictly under the bar).
   Sub-50% calls (OVER 5/6/7, UNDER 2/3/4) are blocked: better win rates, no long-shot bets. */
const overChance = (b) => (9 - b) * 10;
const underChance = (b) => b * 10;
const sideOk = (side, b) => (side === "over" ? overChance(b) : underChance(b)) >= 50;
const DiceDuel = ({ runContract, busy }) => {
  const [bar, setBar] = useState(4);
  const [rolling, setRolling] = useState(false);
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ w: 0, l: 0, net: 0 });
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);
  const auto = useAutoRun("dice");

  const roll = async (side, barOverride) => {
    if (rolling) return null;
    const b = barOverride ?? bar;
    if (!sideOk(side, b)) {
      toast.error(`⛔ ${side.toUpperCase()} ${b} is a sub-50% call — blocked`, { description: "Dice Duel only fires 50%+ win-chance sides: OVER on bars 2–4, UNDER on bars 5–7.", duration: 4500 });
      return null;
    }
    setRolling(true); setRes(null);
    const r = await runContract({
      contractType: side === "over" ? "DIGITOVER" : "DIGITUNDER",
      barrier: b, side: `${side.toUpperCase()} ${b}`, label: "Dice Duel", symbol: symRef.current,
    });
    setRolling(false);
    if (!r) return null;
    setRes({ ...r, side });
    setTally((t) => ({ w: t.w + (r.won ? 1 : 0), l: t.l + (r.won ? 0 : 1), net: Math.round((t.net + r.profit) * 100) / 100 }));
    return r;
  };

  const mult = res && res.stake ? (res.payout / res.stake).toFixed(2) : null;

  return (
    <div data-testid="dice-game" className="space-y-4">
      <HowToPlay id="dice"
        steps={[
          "The ⚡ Signal Engine checks every 50%+ bar & side combo on all 10 markets, compares each one's recent hit rate to its typical rate, and locks the biggest gap — or says WAIT when digits run close to typical.",
          "Tap ⚡ BET THE SIGNAL to take the detected bar + side, or set your own bar: OVER wins above it, UNDER wins below it (exactly the bar = both lose).",
          "50%+ only: bars 2–4 fire OVER (70/60/50% win chance), bars 5–7 fire UNDER (50/60/70%). Long-shot sides are blocked — better win rates, steadier sessions.",
          "Tap OVER or UNDER — the dealer shakes the cup while the real contract settles, then the die shows the actual digit.",
        ]}
        win="the next tick's last digit lands on your side of the bar."
        payout="live Deriv odds — shown as ×multiplier after each win. Higher win-chance sides pay a smaller multiplier."
        tip="every allowed call wins at least 50% of the time — the payout adjusts to match, so manage your stake and bank your wins." />

      <MarketScanner id="dice" mode="hilo" bar={bar} onChange={onScan}
        note={`O/U = share of the last 50 digits above/below your bar (${bar}) · history only, NOT a prediction`} />

      {/* 🎲 the table — casino felt */}
      <div className="relative rounded-xl border-2 border-[#F7B731]/50 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 50% 30%, #11241c 0%, #0B0E14 80%)", boxShadow: "0 0 22px rgba(247,183,49,.1), inset 0 0 40px rgba(0,0,0,.55)" }}>
        <MarqueeRow pos="top" />
        <MarqueeRow pos="bottom" />
        <StageFX outcome={res && !rolling ? (res.won ? "win" : "loss") : null} />
        <svg viewBox="0 0 320 108" className="w-full h-28">
          <Dealer rolling={rolling} />
        </svg>
        {/* the die — tumbles in 3D while the contract settles, lands on the real digit */}
        <div className="absolute left-1/2 translate-x-8 bottom-4" style={{ perspective: "500px" }}>
          {rolling ? (
            <div className="die-tumble"><DieFace v={Math.floor(Math.random() * 6) + 1} /></div>
          ) : res ? (
            <div className="die-land"><DieFace v={res.digit ?? 0} won={res.won} /></div>
          ) : (
            <DieFace v={bar} />
          )}
        </div>
        <div className="absolute top-2 left-3">
          {rolling && <p className="text-[11px] font-mono text-[#F7B731] animate-pulse">dealer shaking — settling the tick…</p>}
          {res && !rolling && (
            <div className="arc-pop">
              <p className={`text-sm font-black font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                {res.won ? "YOU WIN 🏆" : "HOUSE WINS"}
              </p>
              <p className={`text-[11px] font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                rolled {res.digit} · {res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}{res.won && mult ? ` (×${mult})` : ""}
              </p>
            </div>
          )}
          {!res && !rolling && <p className="text-[10px] font-mono text-white/50">set the bar · call over or under</p>}
        </div>
      </div>

      <div className="flex justify-center gap-1.5 flex-wrap">
        {BARS.map((b) => (
          <button key={b} data-testid={`dice-bar-${b}`} disabled={busy || rolling} onClick={() => setBar(b)}
            className={`px-3 h-9 rounded-lg border font-mono font-bold text-sm transition ${bar === b ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]" : "border-white/15 text-white/60"}`}>
            {b}
          </button>
        ))}
      </div>
      <p className="text-center text-[9px] font-mono text-white/40 -mt-2">🎯 50%+ win chance only — bars 2–4 fire OVER · bars 5–7 fire UNDER · long-shot sides are blocked</p>

      <AutoRunBar id="dice" auto={auto} noun="rolls" />

      {(() => {
        const sig = mkt?.signal;
        const sigOk = sig && !sig.wait && sideOk(sig.side === "OVER" ? "over" : "under", sig.bar);
        return sigOk ? (
        <button data-testid="dice-signal-bet" disabled={busy || rolling || auto.active}
          onClick={() => {
            let lastS = sig.side === "OVER" ? "over" : "under";
            let lastB = sig.bar;
            setBar(lastB);
            auto.burst(roll, () => {
              const s = mktRef.current?.signal;
              if (s && !s.wait && s.bar != null && sideOk(s.side === "OVER" ? "over" : "under", s.bar)) {
                lastS = s.side === "OVER" ? "over" : "under"; lastB = s.bar; setBar(s.bar);
              }
              return lastS;
            }, () => lastB);
          }}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Bet the signal — {sig.side} {sig.bar} · hit {sig.recent}% recently (typical {sig.theo}%){auto.runs > 1 ? " · follows the best signal" : ""}
        </button>
        ) : (
        <div data-testid="dice-signal-wait" className="w-full h-12 rounded-lg border border-dashed border-white/15 grid place-items-center text-[10px] font-mono text-white/45">
          ⏳ no clear 50%+ setup right now — digits are running close to typical on all markets
        </div>
        );
      })()}

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="dice-under" disabled={busy || rolling || auto.active || !sideOk("under", bar)} onClick={() => auto.burst(roll, "under")}
          className="h-16 rounded-lg bg-[#131722] border border-[#2962FF]/50 hover:bg-[#2962FF]/10 text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="font-black text-sm uppercase">⬇ Under {bar}{sideOk("under", bar) && mkt && mkt.underPct >= mkt.overPct + 10 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/60">{sideOk("under", bar) ? `wins on 0–${bar - 1} · ${underChance(bar)}% chance${mkt ? ` · recent U ${mkt.underPct}%` : ""}` : `🔒 ${underChance(bar)}% chance — below 50%, blocked`}</span>
        </button>
        <button data-testid="dice-over" disabled={busy || rolling || auto.active || !sideOk("over", bar)} onClick={() => auto.burst(roll, "over")}
          className="h-16 rounded-lg bg-[#131722] border border-[#F7B731]/50 hover:bg-[#F7B731]/10 text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="font-black text-sm uppercase">⬆ Over {bar}{sideOk("over", bar) && mkt && mkt.overPct >= mkt.underPct + 10 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/60">{sideOk("over", bar) ? `wins on ${bar + 1}–9 · ${overChance(bar)}% chance${mkt ? ` · recent O ${mkt.overPct}%` : ""}` : `🔒 ${overChance(bar)}% chance — below 50%, blocked`}</span>
        </button>
      </div>

      <p data-testid="dice-tally" className="text-center text-[11px] font-mono text-white/60">
        Wins <b className="text-[#089981]">{tally.w}</b> · Losses <b className="text-[#F23645]">{tally.l}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
      </p>
      <p className="text-center text-[10px] font-mono text-white/40">real Deriv Over/Under digit contracts on the locked market · only 50%+ win-chance sides can fire · each roll is random · losses possible</p>
    </div>
  );
};

export default DiceDuel;
